import { useState, useEffect } from "react";
import { useApp } from "@/contexts/AppContext";
import api, { formatCurrency, formatDate } from "@/lib/api";
import { Download, Eye, ChevronLeft, ChevronRight } from "lucide-react";

export default function JournalsPage() {
  const { activeEntity, getEntityName } = useApp();
  const [journals, setJournals] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const pageSize = 15;

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const params = { page, page_size: pageSize };
        if (activeEntity) params.entity_id = activeEntity.id;
        if (typeFilter) params.journal_type = typeFilter;
        const { data } = await api.get("/journals", { params });
        setJournals(data.journals);
        setTotal(data.total);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, [activeEntity, page, typeFilter]);

  const downloadCsv = async (journalId, reference) => {
    try {
      const { data } = await api.get(`/journals/${journalId}/csv`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `${reference}.csv`;
      link.click();
    } catch (e) { alert("Error downloading CSV"); }
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-4" data-testid="journals-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold text-[#0B1020]">Journals</h1>
          <p className="text-sm text-[#6B7280] mt-1">{total} journal entries</p>
        </div>
        <select className="stratum-input" value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }} data-testid="journal-type-filter">
          <option value="">All Types</option>
          <option value="DEPRECIATION">Depreciation</option>
          <option value="DISPOSAL">Disposal</option>
          <option value="CAPITALISATION">Capitalisation</option>
        </select>
      </div>

      <div className="stratum-card overflow-hidden">
        <table className="w-full stratum-table" data-testid="journals-table">
          <thead>
            <tr>
              <th>Reference</th>
              <th>Type</th>
              <th>Date</th>
              <th>Description</th>
              <th className="text-right">Debit</th>
              <th className="text-right">Credit</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="text-center py-8 text-[#6B7280]">Loading...</td></tr>
            ) : journals.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-8 text-[#6B7280]">No journals found. Run depreciation or post a disposal to create journals.</td></tr>
            ) : journals.map((j) => (
              <>
                <tr key={j.id} className="cursor-pointer" onClick={() => setExpanded(expanded === j.id ? null : j.id)} data-testid={`journal-row-${j.reference}`}>
                  <td className="font-mono text-sm font-medium text-[#0B1020]">{j.reference}</td>
                  <td>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      j.type === "DEPRECIATION" ? "bg-[#1E40AF]/10 text-[#1E40AF]" :
                      j.type === "DISPOSAL" ? "bg-[#991B1B]/10 text-[#991B1B]" :
                      "bg-[#065F46]/10 text-[#065F46]"
                    }`}>{j.type}</span>
                  </td>
                  <td className="text-sm font-mono tabular-nums">{formatDate(j.date)}</td>
                  <td className="text-sm max-w-[250px] truncate">{j.description}</td>
                  <td className="text-right font-mono tabular-nums text-sm">{formatCurrency(j.total_debit)}</td>
                  <td className="text-right font-mono tabular-nums text-sm">{formatCurrency(j.total_credit)}</td>
                  <td>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${j.is_balanced ? "bg-[#065F46]/10 text-[#065F46]" : "bg-[#991B1B]/10 text-[#991B1B]"}`}>
                      {j.is_balanced ? "Balanced" : "Unbalanced"}
                    </span>
                  </td>
                  <td>
                    <button onClick={(e) => { e.stopPropagation(); downloadCsv(j.id, j.reference); }} className="p-1.5 rounded hover:bg-[#FAF8F4]" title="Download CSV" data-testid={`download-journal-${j.reference}`}>
                      <Download size={14} className="text-[#6B7280]" />
                    </button>
                  </td>
                </tr>
                {expanded === j.id && (
                  <tr key={`${j.id}-detail`}>
                    <td colSpan={8} className="bg-[#FAF8F4] p-4">
                      <div className="space-y-1">
                        <div className="flex justify-between text-xs font-medium text-[#6B7280] pb-2 border-b border-[#E8E2D5]">
                          <span className="w-1/2">Account</span>
                          <span className="w-1/4 text-right">Debit</span>
                          <span className="w-1/4 text-right">Credit</span>
                        </div>
                        {j.lines?.map((line, i) => (
                          <div key={i} className="flex justify-between text-sm py-1">
                            <span className="w-1/2 text-[#1A1A1A]">{line.account}</span>
                            <span className="w-1/4 text-right font-mono tabular-nums">{line.debit > 0 ? formatCurrency(line.debit) : ""}</span>
                            <span className="w-1/4 text-right font-mono tabular-nums">{line.credit > 0 ? formatCurrency(line.credit) : ""}</span>
                          </div>
                        ))}
                        <div className="flex justify-between text-sm font-medium pt-2 border-t border-[#E8E2D5]">
                          <span className="w-1/2">Total</span>
                          <span className="w-1/4 text-right font-mono tabular-nums">{formatCurrency(j.total_debit)}</span>
                          <span className="w-1/4 text-right font-mono tabular-nums">{formatCurrency(j.total_credit)}</span>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </>
            ))}
          </tbody>
        </table>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-[#E8E2D5]">
            <span className="text-sm text-[#6B7280]">Page {page} of {totalPages}</span>
            <div className="flex gap-2">
              <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} className="p-2 rounded-lg border border-[#E8E2D5] disabled:opacity-30"><ChevronLeft size={16} /></button>
              <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages} className="p-2 rounded-lg border border-[#E8E2D5] disabled:opacity-30"><ChevronRight size={16} /></button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
